import api from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/profiles`;

const PROFILE_LABELS = {
  ROLE_OWNER: "Proprietário",
  ROLE_ADMIN: "Administrador",
  ROLE_USER: "Usuário",
  ROLE_GUEST: "Convidado",
};

/**
 * Profile Service
 * Perfis de acesso (bf_role) e os menus que cada perfil acessa (ProfileController - /profiles).
 * O vínculo perfil x usuário é feito em /users (campo roles).
 * Requer ROLE_ADMIN ou ROLE_OWNER no backend.
 */
class ProfileService {
  /**
   * A API sempre devolve { data: [...] }, inclusive para um único registro
   * @param {object} response - Axios response
   * @returns {object|null}
   */
  extractOne(response) {
    const payload = response.data?.data ?? response.data;
    return Array.isArray(payload) ? (payload[0] ?? null) : payload;
  }

  /**
   * Listar perfis com seus menus
   * @returns {Promise<Array>} [{ id, name, menus: string[], system }]
   */
  async getAllProfiles() {
    try {
      const response = await api.get(API_BASE);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar perfis:", error);
      throw error;
    }
  }

  /**
   * Buscar perfil por ID
   * @param {number} id - Profile ID
   * @returns {Promise<object>}
   */
  async getProfileById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar perfil ${id}:`, error);
      throw error;
    }
  }

  /**
   * Criar perfil
   * @param {object} profileData - { name, menus: string[] }
   * @returns {Promise<object>}
   */
  async createProfile(profileData) {
    try {
      const response = await api.post(
        API_BASE,
        profileData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar perfil:", error);
      throw error;
    }
  }

  /**
   * Atualizar perfil e permissões de menu (menus omitidos mantêm os atuais)
   * @param {number} id - Profile ID
   * @param {object} profileData - { name, menus?: string[] }
   * @returns {Promise<object>}
   */
  async updateProfile(id, profileData) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}`,
        profileData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar perfil ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remover perfil (perfis do sistema ou vinculados a usuários não podem ser removidos)
   * @param {number} id - Profile ID
   * @returns {Promise<void>}
   */
  async deleteProfile(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar perfil ${id}:`, error);
      throw error;
    }
  }

  /**
   * Normaliza nome do perfil como o backend salva (maiúsculas + prefixo ROLE_)
   * @param {string} name
   * @returns {string}
   */
  normalizeName(name) {
    const value = String(name || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");
    if (!value) return "";
    return value.startsWith("ROLE_") ? value : `ROLE_${value}`;
  }

  /**
   * Nome amigável do perfil (ex.: ROLE_ATENDENTE -> Atendente)
   * @param {string} name
   * @returns {string}
   */
  getProfileLabel(name) {
    const normalized = this.normalizeName(name);
    if (PROFILE_LABELS[normalized]) return PROFILE_LABELS[normalized];
    return normalized
      .replace(/^ROLE_/, "")
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(" ");
  }

  /**
   * Valida dados do perfil
   * @param {object} data - { name }
   * @returns {object} erros por campo
   */
  validateProfileData(data) {
    const errors = {};
    const name = String(data.name || "").trim();

    if (!name) {
      errors.name = "Nome do perfil é obrigatório";
    } else if (!/^(ROLE_)?[A-Z0-9_]+$/i.test(name.replace(/\s+/g, "_"))) {
      errors.name = "Use apenas letras sem acento, números e _";
    } else if (this.normalizeName(name).length > 50) {
      errors.name = "Nome deve ter no máximo 50 caracteres";
    }

    return errors;
  }
}

// Export singleton instance
const profileService = new ProfileService();
export default profileService;
